import { useState, useCallback, useRef, useEffect } from 'react'
import { useAuth } from './useAuth'
import { supabase } from '@/integrations/supabase/client' // Single source of truth for Supabase
import { RealtimeClient } from 'openai-realtime-api'
import { useTranslation } from 'react-i18next'
import type { Json } from '@/integrations/supabase/types'

interface RealtimeState {
  isConnected: boolean
  isListening: boolean
  isSpeaking: boolean
  currentTranscript: string
  error: string | null
}

export function useOpenAIRealtime(conversationId?: string, voiceOverride?: string) {
  const { user, isLoading: isAuthLoading } = useAuth()
  const { i18n } = useTranslation()

  // Unique mount ID for detecting duplicate instances
  const mountIdRef = useRef(Math.random().toString(36).slice(2, 9))

  const [state, setState] = useState<RealtimeState>({
    isConnected: false,
    isListening: false,
    isSpeaking: false,
    currentTranscript: '',
    error: null
  })

  const [isMuted, setIsMuted] = useState(false)
  const isMutedRef = useRef(isMuted) // Ref for idempotent toggle checks
  const lastToggleAtRef = useRef(0) // Debounce timestamp

  const clientRef = useRef<RealtimeClient | null>(null)
  const mediaStreamRef = useRef<MediaStream | null>(null)
  const audioContextRef = useRef<AudioContext | null>(null)
  const processorRef = useRef<ScriptProcessorNode | null>(null)
  const playbackContextRef = useRef<AudioContext | null>(null)
  const playbackQueueRef = useRef<Int16Array[]>([])
  const isPlayingRef = useRef(false)
  const playbackGenerationIdRef = useRef(0)
  const audioChunksReceivedRef = useRef(0)
  const currentMessageIdRef = useRef<string | null>(null)
  const initializingRef = useRef(false) // Guard against double initialization
  const initAbortControllerRef = useRef<AbortController | null>(null)
  const playbackSuspendedByBargeInRef = useRef(false)
  const stoppedDueToDisconnectRef = useRef(false) // Prevent repeated stop on disconnect
  const activeSourcesRef = useRef<AudioBufferSourceNode[]>([])
  const localAudioBufferRef = useRef<Int16Array[]>([])
  const reconnectingRef = useRef(false)
  const lastSeenConnectedAtRef = useRef<number>(Date.now())
  const reconnectTimeoutMs = 5000
  const isSocketConnectedRef = useRef(false)
  const cleanupHandlersRef = useRef<(() => void) | null>(null)

  const MAX_PLAYBACK_CHUNKS = 100 // ~several seconds of audio, tune as needed
  const MAX_LOCAL_BUFFER_BLOCKS = 200 // cap buffered mic frames to avoid leaks

  // Helper: Decode base64 delta to Int16Array
  const base64ToInt16Array = useCallback((b64: string): Int16Array => {
    // Strip possible data URI prefix
    const cleaned = b64.replace(/^data:audio\/\w+;base64,/, '')
    const binary = atob(cleaned)
    const len = binary.length

    // Ensure even length for Int16
    const buf = new ArrayBuffer(len)
    const view = new Uint8Array(buf)
    for (let i = 0; i < len; i++) view[i] = binary.charCodeAt(i)

    // Create Int16 view, little-endian decode
    const out = new Int16Array(len / 2)
    const dv = new DataView(buf)
    for (let i = 0; i < out.length; i++) {
      out[i] = dv.getInt16(i * 2, true)
    }
    return out
  }, [])

  // Audio playback function - CRITICAL: This plays OpenAI voice through speakers
  const playAudioQueue = useCallback(async () => {
    console.log('[REALTIME-AUDIO] 🎵 playAudioQueue called, isPlaying:', isPlayingRef.current, 'queueLength:', playbackQueueRef.current.length, 'muted:', isMutedRef.current)

    if (isPlayingRef.current || playbackQueueRef.current.length === 0) {
      console.log('[REALTIME-AUDIO] ⏭️ Skipping - already playing or empty queue')
      return
    }

    if (isMutedRef.current) {
      console.log('[REALTIME-AUDIO] 🔇 Muted - clearing queue without playing')
      playbackQueueRef.current = []
      return
    }

    isPlayingRef.current = true
    console.log('[REALTIME-AUDIO] 🔊 Starting audio playback...')

    try {
      if (!playbackContextRef.current) {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const AudioContext = window.AudioContext || (window as any).webkitAudioContext
        playbackContextRef.current = new AudioContext({ sampleRate: 24000 })

        console.log('[REALTIME-AUDIO] 🎹 Audio context created:', {
          state: playbackContextRef.current.state,
          sampleRate: playbackContextRef.current.sampleRate,
          destination: playbackContextRef.current.destination,
          channelCount: playbackContextRef.current.destination.maxChannelCount,
          outputLatency: playbackContextRef.current.outputLatency || 'unknown'
        })
      }

      const context = playbackContextRef.current

      // CRITICAL: Resume context if suspended (required for mobile Safari)
      if (context.state === 'suspended') {
        console.log('[REALTIME-AUDIO] ⚠️ Context suspended, attempting resume...')
        try {
          await context.resume()
          console.log('[REALTIME-AUDIO] ✅ Audio context resumed successfully, state:', context.state)
        } catch (err) {
          console.error('[REALTIME-AUDIO] ❌ Failed to resume context:', err)
          isPlayingRef.current = false
          return
        }
      }

      console.log('[REALTIME-AUDIO] 📊 Context state:', context.state, 'currentTime:', context.currentTime)

      if (context.state !== 'running') {
        console.error('[REALTIME-AUDIO] ❌ Context not running! State:', context.state)
        isPlayingRef.current = false
        return
      }

      let chunksPlayed = 0
      // Prebuffer up to ~1.5s of audio before starting playback for smoother start
      const samplesPerSecond = 24000
      let prebuffered = 0
      const prebufferTarget = Math.floor(samplesPerSecond * 1.5) // 1.5s for smooth playback
      const prebufferStore: Int16Array[] = []
      while (playbackQueueRef.current.length > 0 && prebuffered < prebufferTarget) {
        const next = playbackQueueRef.current.shift()
        if (next && next.length) {
          prebufferStore.push(next)
          prebuffered += next.length
        }
      }
      // Put back prebuffered data at the front (maintain order)
      playbackQueueRef.current = [...prebufferStore, ...playbackQueueRef.current]
      while (playbackQueueRef.current.length > 0) {
        const audioData = playbackQueueRef.current.shift()
        if (!audioData || audioData.length === 0) {
          console.warn('[REALTIME-AUDIO] ⚠️ Empty audio data, skipping')
          continue
        }

        try {
          console.log('[REALTIME-AUDIO] 🎼 Processing chunk', chunksPlayed + 1, 'samples:', audioData.length)

          // Convert Int16Array to Float32Array
          const float32Data = new Float32Array(audioData.length)
          for (let i = 0; i < audioData.length; i++) {
            float32Data[i] = audioData[i] / (audioData[i] < 0 ? 0x8000 : 0x7FFF)
          }

          // Create audio buffer
          const buffer = context.createBuffer(1, float32Data.length, 24000)
          buffer.getChannelData(0).set(float32Data)

          console.log('[REALTIME-AUDIO] 🎚️ Buffer created, duration:', buffer.duration, 'seconds')

          const source = context.createBufferSource()
          source.buffer = buffer

          // CRITICAL: Connect to speaker output
          source.connect(context.destination)
          console.log('[REALTIME-AUDIO] 🔌 Source connected to destination (speakers)')

          // Play audio and wait for it to finish
          await new Promise<void>((resolve, reject) => {
            const timeout = setTimeout(() => {
              console.error('[REALTIME-AUDIO] ⏱️ Playback timeout!')
              reject(new Error('Playback timeout'))
            }, 8000) // 8 second timeout (increased for slower clients)

            // Track active source for immediate stop during barge-in
            activeSourcesRef.current.push(source)

            source.onended = () => {
              clearTimeout(timeout)
              // Remove from active list
              const idx = activeSourcesRef.current.indexOf(source)
              if (idx !== -1) activeSourcesRef.current.splice(idx, 1)
              console.log('[REALTIME-AUDIO] ✅ Chunk', chunksPlayed + 1, 'finished playing')
              resolve()
            }

            // Note: AudioBufferSourceNode doesn't have onerror - errors throw from start()

            try {
              source.start(0)
              console.log('[REALTIME-AUDIO] ▶️ Source.start() called for chunk', chunksPlayed + 1)
            } catch (startErr) {
              clearTimeout(timeout)
              console.error('[REALTIME-AUDIO] ❌ Error calling start():', startErr)
              // Ensure we remove from active list if start failed
              const idx = activeSourcesRef.current.indexOf(source)
              if (idx !== -1) activeSourcesRef.current.splice(idx, 1)
              reject(startErr)
            }
          })

          chunksPlayed++
        } catch (error) {
          console.error('[REALTIME-AUDIO] ❌ Audio chunk playback error:', error)
          break
        }
      }

      console.log('[REALTIME-AUDIO] 🎉 Playback complete! Played', chunksPlayed, 'chunks')
    } catch (error) {
      console.error('[REALTIME-AUDIO] ❌ Fatal playback error:', error)
    } finally {
      isPlayingRef.current = false
    }
  }, [])

  // Extract and save memories from voice conversations
  const extractAndSaveMemories = useCallback(async (transcript: string, conversationId: string, userId: string, categoryId?: string) => {
    try {
      console.log('[REALTIME-MEMORY] Extracting memories from transcript:', transcript.substring(0, 100) + '...')

      // Call the AI conversation function to extract memories
      const { data: { session: authSession } } = await supabase.auth.getSession()
      if (!authSession?.access_token) {
        console.warn('[REALTIME-MEMORY] No auth session for memory extraction')
        return
      }

      const memoryResponse = await fetch(
        'https://lpwjdezzaylhiuyyugqt.supabase.co/functions/v1/ai-conversation',
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${authSession.access_token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            conversationId,
            userResponse: transcript,
            category: categoryId,
            userId,
            isVoiceMessage: true,
            extractMemoriesOnly: true // Flag to indicate we only want memory extraction
          })
        }
      )

      if (memoryResponse.ok) {
        const memoryData = await memoryResponse.json()
        if (memoryData.memoryUpdates && Object.keys(memoryData.memoryUpdates).length > 0) {
          console.log('[REALTIME-MEMORY] ✅ Extracted memories:', Object.keys(memoryData.memoryUpdates))

          // CRITICAL: Merge new facts into existing category memory (don't replace!)
          if (categoryId) {
            const { data: existingCat } = await supabase
              .from('user_memories')
              .select('key_facts')
              .eq('user_id', userId)
              .eq('category', categoryId)
              .maybeSingle()

            const mergedCatFacts = {
              ...(existingCat?.key_facts as Record<string, unknown> || {}),
              ...memoryData.memoryUpdates
            }

            await supabase.from('user_memories').upsert({
              user_id: userId,
              category: categoryId,
              key_facts: mergedCatFacts
            })
            console.log('[REALTIME-MEMORY] 📂 Category memory merged, total keys:', Object.keys(mergedCatFacts).length)
          }

          // Save important facts to global memory (also merge, not replace)
          const globalFacts: Record<string, unknown> = {}
          const globalKeys = [
            'persona_of_faith', 'faith_background', 'religion', 'marital_status',
            'living_situation', 'personality', 'age', 'gender', 'occupation',
            'family_status', 'health_conditions', 'goals', 'interests',
            'name', 'preferred_name', 'children', 'grandchildren', 'spouse',
            'location', 'country', 'city', 'pets', 'hobbies'
          ]

          Object.entries(memoryData.memoryUpdates).forEach(([key, value]) => {
            if (globalKeys.includes(key) || key.includes('faith') || key.includes('religion') ||
              key.includes('marital') || key.includes('family') || key.includes('personality') ||
              key.includes('children') || key.includes('location') || key.includes('friend')) {
              globalFacts[key] = value
            }
          })

          if (Object.keys(globalFacts).length > 0) {
            // Fetch existing global memory and merge
            const { data: existingGlobal } = await supabase
              .from('user_memories')
              .select('key_facts')
              .eq('user_id', userId)
              .eq('category', 'global')
              .maybeSingle()

            const mergedGlobalFacts = {
              ...(existingGlobal?.key_facts as Record<string, unknown> || {}),
              ...globalFacts
            }

            console.log('[REALTIME-MEMORY] 🌍 Saving merged global facts:', Object.keys(mergedGlobalFacts))
            await supabase.from('user_memories').upsert({
              user_id: userId,
              category: 'global',
              key_facts: mergedGlobalFacts as Json
            })
          }
        }
      } else {
        console.warn('[REALTIME-MEMORY] Failed to extract memories:', memoryResponse.status)
      }
    } catch (error) {
      console.error('[REALTIME-MEMORY] Error extracting memories:', error)
    }
  }, [])

  // Initialize Realtime client
  const initializeSession = useCallback(async (signal?: AbortSignal) => {
    console.log('[REALTIME] 🚀 Starting initializeSession with conversationId:', conversationId)

    // Guard: prevent double initialization
    if (initializingRef.current) {
      console.log('[REALTIME] Init already in progress, skipping')
      return
    }

    // Abort previous init if any
    if (initAbortControllerRef.current) initAbortControllerRef.current.abort()
    initAbortControllerRef.current = new AbortController()
    const activeSignal = signal || initAbortControllerRef.current.signal

    // Guard: skip if already connected (isConnected is a getter, not a function)
    if (clientRef.current && clientRef.current.isConnected) {
      console.log('[REALTIME] Client already connected, skipping init')
      return
    }

    // CRITICAL: With AuthContext, auth should be instant. If still loading, wait briefly.
    if (isAuthLoading) {
      console.log('[REALTIME] ⏳ Auth still loading (should be instant with AuthContext), waiting...')
      setState(prev => ({ ...prev, error: 'Waiting for authentication...' }))
      return
    }

    // If auth is done but no user, this is a real auth failure (not a race condition)
    if (!user) {
      console.error('[REALTIME] ❌ Auth complete but no user - not authenticated')
      setState(prev => ({ ...prev, error: 'Please sign in to use voice mode' }))
      return
    }

    if (!conversationId) {
      console.error('[REALTIME] ❌ No conversation ID provided')
      setState(prev => ({ ...prev, error: 'Conversation not found' }))
      return
    }

    console.log('[REALTIME] ✅ Conversation ID provided:', conversationId)

    // Verify conversation exists in database
    try {
      const { data: conversationData, error: conversationError } = await supabase
        .from('conversations')
        .select('id, title')
        .eq('id', conversationId)
        .single()

      if (conversationError) {
        console.error('[REALTIME] ❌ Conversation not found in database:', conversationError)
        setState(prev => ({ ...prev, error: 'Conversation not found in database' }))
        return
      }

      console.log('[REALTIME] ✅ Conversation verified in database:', conversationData.title)
    } catch (verifyError) {
      console.error('[REALTIME] ❌ Error verifying conversation:', verifyError)
      setState(prev => ({ ...prev, error: 'Failed to verify conversation' }))
      return
    }

    initializingRef.current = true
    console.log('[REALTIME] 🚀 Starting initialization with user:', user.id, 'conversation:', conversationId)

    try {
      console.log('[REALTIME] Initializing OpenAI Realtime session...')

      // Get conversation context from database
      const [convResult, historyResult] = await Promise.all([
        supabase
          .from('conversations')
          .select('*, category:categories(*)')
          .eq('id', conversationId)
          .single(),
        supabase
          .from('messages')
          .select('*')
          .eq('conversation_id', conversationId)
          .order('created_at', { ascending: true })
          .limit(10)
      ])

      if (convResult.error) throw convResult.error

      const conversation = convResult.data
      const messageHistory = historyResult.data || []

      // Get memories after we have the conversation data (id-first, then name fallback)
      const [memoriesById, globalMemoryResult] = await Promise.all([
        supabase
          .from('user_memories')
          .select('*')
          .eq('user_id', user.id)
          .eq('category', conversation.category?.id || '')
          .maybeSingle(),
        supabase
          .from('user_memories')
          .select('*')
          .eq('user_id', user.id)
          .eq('category', 'global')
          .maybeSingle()
      ])

      let memories = memoriesById.data
      if (!memories || !memories.key_facts) {
        try {
          const byName = await supabase
            .from('user_memories')
            .select('*')
            .eq('user_id', user.id)
            .eq('category', conversation.category?.name || '')
            .maybeSingle()
          if (byName.data) memories = byName.data
        } catch { /* ignore */ }
      }
      const globalMemories = globalMemoryResult.data

      // Load conversation flow for category-specific instructions (non-blocking with timeout)
      const preferredPersona = user.user_metadata?.preferred_persona || 'Sidekick'
      let flow = null

      try {
        // Add timeout to prevent blocking
        const flowPromise = supabase
          .from('conversation_flows')
          .select('*')
          .eq('category_id', conversation.category?.id)
          .eq('persona', preferredPersona)
          .maybeSingle()

        const timeoutPromise = new Promise<never>((_, reject) =>
          setTimeout(() => reject(new Error('Flow query timeout')), 2000)
        )

        const flowResult = await Promise.race([flowPromise, timeoutPromise])
        flow = flowResult.data || null

        console.log('[REALTIME] Loaded conversation flow:', {
          category: conversation.category?.name,
          persona: preferredPersona,
          hasFlow: !!flow,
          conversationType: flow?.conversation_type
        })
      } catch (flowError) {
        console.warn('[REALTIME] Failed to load conversation flow (timeout or error), using generic instructions:', flowError)
        // Continue without flow - will use generic instructions
      }

      // Get ephemeral token from your Edge Function
      const { data: { session: authSession } } = await supabase.auth.getSession()
      if (!authSession?.access_token) {
        throw new Error('No authentication token')
      }

      // Determine voice based on user selection or language
      // Available voices: alloy (neutral), echo (male), fable (British), onyx (deep male), nova (female), shimmer (soft female)
      const selectedVoice = voiceOverride || user?.user_metadata?.preferred_voice || 'alloy'

      console.log('[REALTIME] 🔑 Fetching ephemeral token from Edge Function...')
      console.log('[REALTIME] 🎤 Selected voice for token request:', selectedVoice)

      const tokenResponse = await fetch(
        'https://lpwjdezzaylhiuyyugqt.supabase.co/functions/v1/voice-realtime-token',
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${authSession.access_token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            voice: selectedVoice
          }),
          signal: activeSignal
        }
      )

      console.log('[REALTIME] Token response status:', tokenResponse.status)

      if (!tokenResponse.ok) {
        const errorText = await tokenResponse.text()
        console.error('[REALTIME] ❌ Token fetch failed:', tokenResponse.status, errorText)

        let errorMsg = 'Failed to get realtime token'
        try {
          const errorData = JSON.parse(errorText)
          errorMsg = errorData.message || errorData.error || errorMsg
        } catch {
          errorMsg = errorText
        }

        throw new Error(`${errorMsg} (Status: ${tokenResponse.status})`)
      }

      const sessionData = await tokenResponse.json() as {
        client_secret: { value: string; expires_at: number }
        url?: string
        model?: string
        expires_at?: number
      }

      console.log('[REALTIME] ✅ Session data received:', {
        hasClientSecret: !!sessionData.client_secret,
        hasValue: !!sessionData.client_secret?.value,
        hasUrl: !!sessionData.url,
        model: sessionData.model,
        tokenType: sessionData.client_secret?.value?.startsWith('eph_') ? 'ephemeral' : 'unknown'
      })

      if (!sessionData.client_secret || !sessionData.client_secret.value) {
        throw new Error('Invalid token response - missing client_secret.value')
      }

      const currentLanguage = i18n.language || 'en'

      // Build context-aware system instructions using conversation flow
      const rawInstructions = flow?.ai_instructions || `You are ${preferredPersona}, a helpful AI assistant.`

      // CRITICAL FIX: Strip the rigid text formatting rules out completely for voice mode
      // All 72 conversation flows have a "CRITICAL FORMATTING RULE" that forces numbered lists,
      // which directly conflicts with natural conversational voice. Remove it.
      const baseInstructions = rawInstructions.replace(/CRITICAL FORMATTING RULE[\s\S]*/i, '').trim();

      // Adapt instructions for voice mode (concise, conversational)
      const voiceAdaptation = `
VOICE MODE ADAPTATION (CRITICAL FOR NATURAL CONVERSATION):
- You are speaking out loud to an older adult. Keep your tone warm, patient, and conversational.
- IGNORE any previous instructions to output numbered lists. NEVER speak numbers, bullet points, or markdown.
- DO NOT read out menus or options like an automated phone system.
- To provide options, you MUST use the "display_visual_options" tool to show them silently on the screen.
- Weave suggestions naturally into your sentences (e.g. "We could talk about X, or look at Y. What sounds best?")
- Keep responses concise (under 3 sentences) and end with a gentle question.

MEMORY COLLECTION (CRITICAL):
- Listen carefully for personal information the user shares.
- Extract key facts like faith background, family status, goals, interests.
- Remember important details for future conversations.`

      const languageAwareInstructions = `${baseInstructions}

${voiceAdaptation}

User Context:
- Name: ${user.user_metadata?.name || 'User'}
- Country: ${user.user_metadata?.country_code || 'US'}
- Preferred Persona: ${preferredPersona}
- Language: ${currentLanguage}

${memories?.key_facts ? `Category-specific memories:\n${JSON.stringify(memories.key_facts, null, 2)}` : ''}

${globalMemories?.key_facts ? `Global memories (cross-category personalization):\n${JSON.stringify(globalMemories.key_facts, null, 2)}` : ''}

Category: ${conversation.category?.name || ''}

${messageHistory.length > 0 ? `Recent conversation:\n${messageHistory.slice(-5).map((m: { role: string; content: string }) => `${m.role}: ${m.content}`).join('\n')}` : ''}

IMPORTANT LANGUAGE INSTRUCTION: The user's current language setting is "${currentLanguage}". You MUST respond in ${currentLanguage === 'en' ? 'English' : currentLanguage === 'es' ? 'Spanish' : currentLanguage === 'fr' ? 'French' : currentLanguage === 'de' ? 'German' : currentLanguage === 'it' ? 'Italian' : currentLanguage === 'pt' ? 'Portuguese' : currentLanguage === 'ja' ? 'Japanese' : currentLanguage === 'ko' ? 'Korean' : currentLanguage === 'zh' ? 'Chinese' : 'the user\'s selected language'}.`

      console.log('[REALTIME] Creating session with context:', {
        conversationId,
        category: conversation.category?.name,
        persona: preferredPersona,
        conversationType: flow?.conversation_type || 'conversational',
        voice: selectedVoice,
        language: currentLanguage,
        hasFlowInstructions: !!flow?.ai_instructions
      })

      // CRITICAL FIX: Use ephemeral token with session URL if provided
      // According to OpenAI docs, ephemeral sessions may include a specific WebSocket URL
      console.log('[REALTIME] Creating client with ephemeral token:', {
        hasToken: !!sessionData.client_secret.value,
        hasUrl: !!sessionData.url,
        tokenPrefix: sessionData.client_secret.value.substring(0, 15) + '...',
        willUseCustomUrl: !!sessionData.url
      })

      const clientConfig: { apiKey: string; dangerouslyAllowAPIKeyInBrowser: boolean; url?: string } = {
        apiKey: sessionData.client_secret.value,
        dangerouslyAllowAPIKeyInBrowser: true
      }

      // If OpenAI provided a specific WebSocket URL, use it
      if (sessionData.url) {
        clientConfig.url = sessionData.url
        console.log('[REALTIME] Using custom WebSocket URL from session')
      }

      console.log('[REALTIME] 🔧 Creating RealtimeClient with config:', {
        hasApiKey: !!clientConfig.apiKey,
        hasUrl: !!clientConfig.url,
        apiKeyPrefix: clientConfig.apiKey?.substring(0, 15) + '...',
        url: clientConfig.url
      })

      const client = new RealtimeClient(clientConfig)
      console.log('[REALTIME] ✅ RealtimeClient created:', {
        clientType: typeof client,
        hasConnect: typeof client.connect === 'function',
        hasUpdateSession: typeof (client as unknown as { updateSession?: unknown }).updateSession === 'function'
      })

      clientRef.current = client

      // Set up event handlers
      const clientAny2 = client as unknown as { on: (event: string, handler: (data: unknown) => void) => void }

      clientAny2.on('conversation.updated', (data: unknown) => {
        const { item, delta } = (data as { item?: { role?: string; formatted?: { transcript?: string } }; delta?: { transcript?: string } }) || {}
        const transcript = delta?.transcript || item?.formatted?.transcript || ''

        if (transcript) {
          console.log('[REALTIME] Conversation updated:', { role: item?.role, transcript })
          setState(prev => ({ ...prev, currentTranscript: transcript }))
        }
      })

      clientAny2.on('conversation.item.completed', async (data: unknown) => {
        console.log('[REALTIME] 🔍 conversation.item.completed event received:', data)
        console.log('[REALTIME] 🔍 Event data type:', typeof data)
        console.log('[REALTIME] 🔍 Event data keys:', data ? Object.keys(data as Record<string, unknown>) : 'no data')

        const { item } = (data as { item?: { id?: string; role?: string; formatted?: { transcript?: string; text?: string } } }) || {}
        if (!item) {
          console.log('[REALTIME] ⚠️ No item in conversation.item.completed event')
          return
        }

        const role = (item.role as 'user' | 'assistant') || 'user'
        const transcript = item.formatted?.transcript || item.formatted?.text || ''

        console.log(`[REALTIME] 🔍 Processing ${role} message:`, {
          hasTranscript: !!transcript,
          transcriptLength: transcript.length,
          conversationId,
          itemId: item.id
        })

        if (transcript) {
          console.log(`[REALTIME] ${role} message completed:`, transcript)

          // Save to database and get message ID
          const { data: messageData, error: messageError } = await supabase.from('messages').insert({
            conversation_id: conversationId,
            role,
            content: transcript,
            metadata: { isVoice: true, source: 'realtime' },
            is_voice_message: true
          }).select('id').single()

          if (messageError) {
            console.error('[REALTIME] Error saving message:', messageError)
          } else {
            console.log(`[REALTIME] ✅ ${role} message saved to database with ID:`, messageData?.id)
          }

          // Store message ID for voice tracking
          if (messageData?.id) {
            currentMessageIdRef.current = messageData.id
          }

          // Extract and save key facts from BOTH user and assistant messages
          if (transcript) {
            try {
              await extractAndSaveMemories(transcript, conversationId, user.id, conversation.category?.id)
            } catch (memoryError) {
              console.warn('[REALTIME] Failed to extract memories:', memoryError)
            }
          }

          // Voice messages are now handled by the main conversation hook via realtime subscription
          // No need to maintain local state - the main chat will automatically update
          console.log(`[REALTIME] ${role} message saved to database, main chat will update via realtime`)

          // Fallback: If realtime subscription fails, we can trigger a manual refresh
          // This ensures voice messages always appear in the chat
          setTimeout(() => {
            console.log(`[REALTIME] 📡 Dispatching voiceMessageSaved event for ${role} message`)
            // Dispatch a custom event that the main conversation hook can listen to
            window.dispatchEvent(new CustomEvent('voiceMessageSaved', {
              detail: { conversationId, role, content: transcript }
            }))
          }, 100)
        }
      })

      clientAny2.on('conversation.interrupted', () => {
        console.log('[REALTIME] Conversation interrupted')
        setState(prev => ({ ...prev, isSpeaking: false }))
      })

      clientAny2.on('error', (error: unknown) => {
        const errorObj = error as { message?: string }
        console.error('[REALTIME] Error:', error)
        setState(prev => ({ ...prev, error: errorObj?.message || 'Realtime error occurred' }))
      })

      // Initialize playback context immediately - CRITICAL FOR AUDIO OUTPUT
      if (!playbackContextRef.current) {
        console.log('[REALTIME-AUDIO] 🎹 Creating initial playback context...')
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const AudioContext = window.AudioContext || (window as any).webkitAudioContext
        playbackContextRef.current = new AudioContext({ sampleRate: 24000 })

        console.log('[REALTIME-AUDIO] ✅ Playback context initialized:', {
          state: playbackContextRef.current.state,
          sampleRate: playbackContextRef.current.sampleRate,
          channels: playbackContextRef.current.destination.maxChannelCount,
          latency: playbackContextRef.current.baseLatency || 'unknown'
        })

        // CRITICAL: Resume context immediately (required for audio playback)
        if (playbackContextRef.current.state === 'suspended') {
          console.log('[REALTIME-AUDIO] ⚠️ Context created in suspended state, resuming...')
          playbackContextRef.current.resume().then(() => {
            console.log('[REALTIME-AUDIO] ✅ Playback context resumed, state:', playbackContextRef.current?.state)
          }).catch(err => {
            console.error('[REALTIME-AUDIO] ❌ Failed to resume on init:', err)
          })
        } else {
          console.log('[REALTIME-AUDIO] ✅ Context created in running state')
        }
      }

      // Connect to OpenAI WebSocket
      console.log('[REALTIME] 🔌 Attempting to connect to OpenAI WebSocket...')
      console.log('[REALTIME] 🔍 Client before connect:', {
        hasClient: !!client,
        clientType: typeof client,
        hasConnect: typeof client.connect === 'function'
      })

      try {
        await client.connect()
        console.log('[REALTIME] ✅ Client connected successfully')
        console.log('[REALTIME] 🔍 Client after connect:', {
          isConnected: client.isConnected,
          clientState: client
        })
      } catch (connectError) {
        console.error('[REALTIME] ❌ Connection failed:', connectError)
        console.error('[REALTIME] 🔍 Connection error details:', {
          error: connectError,
          errorType: typeof connectError,
          errorMessage: connectError instanceof Error ? connectError.message : 'Unknown error',
          errorStack: connectError instanceof Error ? connectError.stack : 'No stack'
        })
        throw new Error(`Failed to connect to OpenAI: ${connectError instanceof Error ? connectError.message : 'Unknown error'}`)
      }

      // Update session after connection with proper config
      console.log('[REALTIME] 🔧 Updating session configuration...')
      try {
        await (client as unknown as { updateSession: (config: unknown) => Promise<void> }).updateSession({
          voice: selectedVoice,
          instructions: languageAwareInstructions,
          modalities: ['text', 'audio'],  // Enable both text and audio output
          temperature: 0.8,
          // CRITICAL: Explicitly request audio output format
          input_audio_format: 'pcm16',
          output_audio_format: 'pcm16',  // ← THIS IS KEY!
          turn_detection: {
            type: 'server_vad',
            threshold: 0.5,
            prefix_padding_ms: 300,
            silence_duration_ms: 800 // Increased from 500ms to 800ms for natural pauses
          },
          // ADDED: Give the AI a way to update the screen silently
          tools: [
            {
              type: "function",
              name: "display_visual_options",
              description: "Displays clickable buttons on the user's screen. Call this tool simultaneously while speaking your natural response to give the user visual choices.",
              parameters: {
                type: "object",
                properties: {
                  options: {
                    type: "array",
                    items: { type: "string" },
                    description: "An array of 3-4 short, contextual phrases the user might want to say next."
                  }
                },
                required: ["options"]
              }
            }
          ],
          tool_choice: "auto",
          input_audio_transcription: {
            model: 'whisper-1'
          }
        })
        console.log('[REALTIME] ✅ Session configuration updated successfully')
      } catch (sessionError) {
        console.error('[REALTIME] ❌ Session update failed:', sessionError)
        throw new Error(`Failed to configure voice session: ${sessionError instanceof Error ? sessionError.message : 'Unknown error'}`)
      }

      console.log('[REALTIME-AUDIO] 🔊 Session configured for AUDIO output:', {
        voice: selectedVoice,
        modalities: ['text', 'audio'],
        output_audio_format: 'pcm16',
        input_audio_format: 'pcm16'
      })

      console.log(`[REALTIME] Session configured with voice: ${selectedVoice}, language: ${currentLanguage}`)
      console.log('[REALTIME-AUDIO] ⚠️ CRITICAL: If you speak and see transcripts but NO "📥 Received audio delta" logs, then OpenAI is sending text-only!')

      // Event handlers - using type assertion to bypass strict typing
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const clientAny = client as any

      const onSessionCreated = () => {
        console.log('[REALTIME] Session created successfully')
        isSocketConnectedRef.current = true
        setState(prev => ({ ...prev, isConnected: true, error: null }))
      }
      clientAny.on('server.session.created', onSessionCreated)

      clientAny.on('server.input_audio_buffer.speech_started', () => {
        console.log('[REALTIME] User started speaking')

        // CRITICAL FIX: Force server to cancel generation immediately to save tokens and audio
        try {
          if (clientRef.current) {
            // Access the underlying realtime socket directly for the most reliable cancel
            const realtime = (clientRef.current as any).realtime;
            if (realtime && typeof realtime.send === 'function') {
              realtime.send('response.cancel', {});
              console.log('[REALTIME-BARGE-IN] 🪓 Sent response.cancel via socket');
            } else if (typeof (clientRef.current as any).cancelResponse === 'function') {
              (clientRef.current as any).cancelResponse();
              console.log('[REALTIME-BARGE-IN] 🪓 Sent cancelResponse via wrapper');
            }
          }
        } catch (e) {
          console.warn('[REALTIME-BARGE-IN] response.cancel failed:', e)
        }

        console.log('[REALTIME-BARGE-IN] 🛑 User started speaking, stopping AI audio')

        // Increment generation ID to invalidate all queued audio
        playbackGenerationIdRef.current++
        console.log(`[REALTIME-BARGE-IN] Generation ID incremented to ${playbackGenerationIdRef.current}`)

        // Clear the entire playback queue
        playbackQueueRef.current = []

        // Stop all active sources with enhanced robustness
        try {
          const list = activeSourcesRef.current
          if (list.length > 0) {
            console.log('[REALTIME-AUDIO] 🛑 Stopping', list.length, 'active source(s)')

            // Create a copy to avoid mutation during iteration
            const sourcesToStop = [...list]
            activeSourcesRef.current = []

            // Stop each source with timeout protection
            const stopPromises = sourcesToStop.map(async (source, index) => {
              try {
                // Stop the source immediately
                source.stop(0)
                console.log(`[REALTIME-AUDIO] ✅ Stopped source ${index + 1}`)
              } catch (stopErr) {
                console.warn(`[REALTIME-AUDIO] ⚠️ Error stopping source ${index + 1}:`, stopErr)
              }

              try {
                // Disconnect the source
                source.disconnect()
                console.log(`[REALTIME-AUDIO] ✅ Disconnected source ${index + 1}`)
              } catch (disconnectErr) {
                console.warn(`[REALTIME-AUDIO] ⚠️ Error disconnecting source ${index + 1}:`, disconnectErr)
              }
            })

            // Wait for all stops to complete (with timeout)
            Promise.race([
              Promise.allSettled(stopPromises),
              new Promise(resolve => setTimeout(resolve, 100)) // 100ms timeout
            ]).catch(err => console.warn('[REALTIME-AUDIO] Stop timeout or error:', err))

            isPlayingRef.current = false
            console.log('[REALTIME-AUDIO] ✅ All sources stopped')
          }
        } catch (err) {
          console.warn('[REALTIME-AUDIO] Error stopping active sources:', err)
          // Force reset even if there was an error
          activeSourcesRef.current = []
          isPlayingRef.current = false
        }

        // Suspend playback context with enhanced error handling
        if (playbackContextRef.current && playbackContextRef.current.state === 'running') {
          playbackContextRef.current.suspend()
            .then(() => {
              playbackSuspendedByBargeInRef.current = true
              console.log('[REALTIME-BARGE-IN] ⏸️ Playback context suspended successfully')
            })
            .catch(err => {
              console.warn('[REALTIME-BARGE-IN] Failed to suspend context:', err)
              // Even if suspend fails, mark as suspended to prevent new audio
              playbackSuspendedByBargeInRef.current = true
            })
        } else if (playbackContextRef.current) {
          console.log('[REALTIME-BARGE-IN] Context not running, state:', playbackContextRef.current.state)
          // Still mark as suspended to prevent new audio
          playbackSuspendedByBargeInRef.current = true
        }

        setState(prev => ({ ...prev, isListening: true, isSpeaking: false }))
      })

      clientAny.on('server.input_audio_buffer.speech_stopped', () => {
        console.log('[REALTIME] User stopped speaking')
        // Resume playback context if we suspended it for barge-in
        if (playbackContextRef.current && playbackContextRef.current.state === 'suspended' && playbackSuspendedByBargeInRef.current) {
          playbackContextRef.current.resume()
            .then(() => {
              playbackSuspendedByBargeInRef.current = false
              console.log('[REALTIME-AUDIO] ▶️ Playback context resumed after barge-in')
            })
            .catch(err => {
              console.warn('[REALTIME-AUDIO] Failed to resume playback context:', err)
              // Even if resume fails, clear the barge-in flag to allow new audio
              playbackSuspendedByBargeInRef.current = false
            })
        } else {
          // Clear barge-in flag even if context wasn't suspended
          playbackSuspendedByBargeInRef.current = false
          console.log('[REALTIME-AUDIO] Cleared barge-in flag (context not suspended)')
        }
        setState(prev => ({ ...prev, isListening: false }))
      })

      // CORRECT: Listen for wrapped realtime events
      clientAny.on('realtime.event', async (data: unknown) => {
        const realtimeEvent = data as { source?: string; event?: { type?: string;[key: string]: unknown } }
        if (realtimeEvent.source !== 'server') return

        const event = realtimeEvent.event
        if (!event) return

        console.log('[REALTIME-AUDIO] 📡 Server event:', event.type)

        // ADDED: Catch the tool call to render the buttons
        if (event.type === 'response.function_call_arguments.done' && event.name === 'display_visual_options') {
          try {
            const args = JSON.parse(event.arguments as string);
            if (args.options && Array.isArray(args.options)) {
              console.log('[REALTIME-UI] 📱 Rendering silent visual options:', args.options);

              // Format them for existing UI components
              const followUpOptions = args.options.map((text: string, i: number) => ({
                id: `voice_opt_${Date.now()}_${i}`,
                text: text,
                payload: text,
                type: 'quick_response'
              }));

              // Dispatch to your main chat component so they render on screen
              window.dispatchEvent(new CustomEvent('voiceOptionsReceived', {
                detail: { conversationId, options: followUpOptions }
              }));
            }
          } catch (err) {
            console.error('[REALTIME-UI] Failed to parse visual options tool call:', err);
          }
        }

        // CRITICAL FIX: Capture user voice transcription
        // OpenAI Realtime API sends user's Whisper transcription via this separate event,
        // NOT through conversation.item.completed (which only reliably fires for assistant)
        if (event.type === 'conversation.item.input_audio_transcription.completed') {
          const userTranscript = (event as any).transcript as string
          const itemId = (event as any).item_id as string

          if (userTranscript && userTranscript.trim()) {
            console.log('[REALTIME-USER] 🎤 User voice transcription received:', userTranscript)

            // Save user message to database
            const { data: msgData, error: msgError } = await supabase.from('messages').insert({
              conversation_id: conversationId,
              role: 'user',
              content: userTranscript.trim(),
              metadata: { isVoice: true, source: 'realtime', itemId },
              is_voice_message: true
            }).select('id').single()

            if (msgError) {
              console.error('[REALTIME-USER] ❌ Error saving user voice message:', msgError)
            } else {
              console.log('[REALTIME-USER] ✅ User voice message saved with ID:', msgData?.id)
            }

            // Extract memories from user speech (this is the most valuable source)
            try {
              await extractAndSaveMemories(userTranscript.trim(), conversationId, user.id, conversation.category?.id)
            } catch (memErr) {
              console.warn('[REALTIME-USER] Memory extraction failed:', memErr)
            }

            // Dispatch refresh event so the chat UI updates
            window.dispatchEvent(new CustomEvent('voiceMessageSaved', {
              detail: { conversationId, role: 'user', content: userTranscript.trim() }
            }))
          }
        }

        // DEBUG: Log FULL event object for audio-related events  
        if (event.type?.includes('audio') || event.type?.includes('content_part')) {
          console.log('[REALTIME-AUDIO] 🔍 FULL EVENT DEBUG:', JSON.stringify(event).substring(0, 500))
        }

        // Handle audio playback - CRITICAL FOR SPEAKER OUTPUT
        if (event.type === 'response.audio.delta') {
          console.log('[REALTIME-AUDIO] 🎉 AUDIO DELTA RECEIVED FROM SERVER!')
          setState(prev => ({ ...prev, isSpeaking: true }))

          const rawDelta = event.delta
          let audioData: Int16Array | undefined

          // Handle multiple possible server representations (robust decoding)
          if (!rawDelta) {
            console.warn('[REALTIME-AUDIO] ⚠️ Received delta event but no delta field')
          } else if (rawDelta instanceof Int16Array) {
            // Already an Int16Array
            audioData = rawDelta
            console.log('[REALTIME-AUDIO] ℹ️ Delta is already Int16Array')
          } else if (Array.isArray(rawDelta)) {
            // Array of numbers
            audioData = Int16Array.from(rawDelta as number[])
            console.log('[REALTIME-AUDIO] ℹ️ Delta converted from number array')
          } else if (typeof rawDelta === 'string') {
            // Expected case: base64 string
            try {
              audioData = base64ToInt16Array(rawDelta)
              console.log('[REALTIME-AUDIO] ℹ️ Delta decoded from base64')
            } catch (err) {
              console.error('[REALTIME-AUDIO] ❌ Failed to decode base64 delta:', err)
            }
          } else if ((rawDelta as unknown as Record<string, unknown>).data) {
            // Wrapped format with .data property
            const maybe = (rawDelta as unknown as Record<string, unknown>).data
            if (typeof maybe === 'string') {
              try {
                audioData = base64ToInt16Array(maybe)
                console.log('[REALTIME-AUDIO] ℹ️ Delta decoded from wrapped base64')
              } catch (err) {
                console.error('[REALTIME-AUDIO] ❌ Failed to decode wrapped base64:', err)
              }
            } else if (Array.isArray(maybe)) {
              audioData = Int16Array.from(maybe as number[])
              console.log('[REALTIME-AUDIO] ℹ️ Delta converted from wrapped array')
            }
          }

          if (audioData && audioData.length > 0) {
            audioChunksReceivedRef.current++
            console.log('[REALTIME-AUDIO] ✅ Valid delta received, size:', audioData.length, 'samples')
            // If we are in barge-in state, ignore incoming audio until the next response cycle
            if (playbackSuspendedByBargeInRef.current) {
              console.log('[REALTIME-AUDIO] ⏩ Ignoring delta due to barge-in')
            } else {
              if (playbackQueueRef.current.length >= MAX_PLAYBACK_CHUNKS) {
                console.warn('[REALTIME-AUDIO] playback queue overflow - trimming oldest')
                playbackQueueRef.current.splice(0, playbackQueueRef.current.length - MAX_PLAYBACK_CHUNKS + 1)
              }
              playbackQueueRef.current.push(audioData)
            }
            console.log('[REALTIME-AUDIO] 📊 Queue size now:', playbackQueueRef.current.length, 'chunks')

            // Fire and forget the player, reset flag on error
            playAudioQueue().catch(err => {
              console.error('[REALTIME-AUDIO] ❌ Error triggering playback:', err)
              isPlayingRef.current = false
            })
          } else {
            console.warn('[REALTIME-AUDIO] ⚠️ Received delta event but no valid Int16Array could be extracted')
          }
        }

        if (event.type === 'response.audio.done') {
          console.log('[REALTIME-AUDIO] 🏁 Assistant finished speaking (audio done)')
          setState(prev => ({ ...prev, isSpeaking: false }))

          // Store message ID for future voice tracking integration
          console.log('[REALTIME-VOICE] Response complete with', audioChunksReceivedRef.current, 'audio chunks')
          audioChunksReceivedRef.current = 0

          // Clear barge-in guard when a response fully completes
          playbackSuspendedByBargeInRef.current = false
        }

        if (event.type === 'response.created') {
          const response = event.response as { id?: string; status?: string; modalities?: string[] } | undefined
          console.log('[REALTIME-AUDIO] 🎬 Response created:', {
            id: response?.id,
            status: response?.status,
            modalities: response?.modalities
          })

          const modalities = response?.modalities || []
          if (!modalities.includes('audio')) {
            console.error('[REALTIME-AUDIO] ❌ PROBLEM FOUND! Response missing "audio" modality!', {
              modalities,
              expected: ['text', 'audio']
            })
            console.error('[REALTIME-AUDIO] 🚨 This explains why no audio is coming through!')
          } else {
            console.log('[REALTIME-AUDIO] ✅ Response HAS audio modality:', modalities)
          }
        }

        if (event.type === 'response.done') {
          console.log('[REALTIME-AUDIO] ✅ Response done:', event)
        }

        if (event.type === 'response.output_item.added') {
          const item = event.item as { type?: string } | undefined
          console.log('[REALTIME-AUDIO] ➕ Output item added, type:', item?.type)
        }

        if (event.type === 'response.output_item.done') {
          const item = event.item as { type?: string; formatted?: { audio?: unknown } } | undefined
          console.log('[REALTIME-AUDIO] ✅ Output item done, type:', item?.type, 'has audio:', !!item?.formatted?.audio)
        }

        if (event.type === 'response.content_part.added') {
          const part = event.part as { type?: string } | undefined
          console.log('[REALTIME-AUDIO] 🧩 Content part added, type:', part?.type)
        }

        if (event.type === 'response.audio_transcript.delta') {
          const delta = event.delta as string | undefined
          console.log('[REALTIME-AUDIO] 📝 Audio transcript delta:', delta)
        }
      })

      // If SDK exposes a disconnect event, wire it
      if (typeof clientAny.on === 'function') {
        const onDisconnect = (err?: unknown) => {
          isSocketConnectedRef.current = false
          console.warn('[REALTIME] Socket disconnected', err)
          setState(prev => ({ ...prev, isConnected: false }))
        }
        try { clientAny.on('disconnect', onDisconnect) } catch { }

        // Store cleanup to remove handlers on re-init/unmount
        cleanupHandlersRef.current = () => {
          try { clientAny.off?.('server.session.created', onSessionCreated) } catch { }
          try { clientAny.off?.('disconnect', onDisconnect) } catch { }
        }
      }

    } catch (error) {
      console.error('[REALTIME] Initialization error:', error)
      setState(prev => ({
        ...prev,
        error: error instanceof Error ? error.message : 'Failed to initialize voice session'
      }))
    } finally {
      // Always reset the initializing flag, even on error
      initializingRef.current = false
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, conversationId, playAudioQueue, isAuthLoading])
  // Note: voiceOverride and i18n.language are intentionally not in dependencies
  // to prevent reinitializing the entire voice session on language/voice changes

  // Start voice conversation
  const startVoiceSession = useCallback(async () => {
    try {
      console.log('[REALTIME] 🎙️ Starting voice session with conversationId:', conversationId)

      // If already listening, don't start again
      if (state.isListening) {
        console.log('[REALTIME] ⚠️ Already listening, skipping start')
        return
      }

      // Initialize client if needed
      if (!clientRef.current) {
        console.log('[REALTIME] 🔌 No client, initializing...')
        try {
          await initializeSession()
          console.log('[REALTIME] ✅ Session initialized successfully')
        } catch (initError) {
          console.error('[REALTIME] ❌ Session initialization failed:', initError)
          throw new Error(`Failed to initialize voice session: ${initError instanceof Error ? initError.message : 'Unknown error'}`)
        }
      }

      // Verify conversation exists before starting voice session
      if (!conversationId) {
        console.warn('[REALTIME] ⚠️ No conversation ID available for voice session - this is normal on category pages')
        setState(prev => ({ ...prev, error: 'No conversation ID available' }))
        return
      }

      // Wait for connection with polling loop (up to 5 seconds)
      if (!clientRef.current?.isConnected) {
        console.log('[REALTIME] ⏳ Client not connected yet, waiting...')
        console.log('[REALTIME] 🔍 Client state:', {
          hasClient: !!clientRef.current,
          isConnected: clientRef.current?.isConnected,
          clientType: typeof clientRef.current
        })

        const startTime = Date.now()
        const maxWaitTime = 5000 // 5 seconds
        const pollInterval = 250 // Check every 250ms

        while (!clientRef.current?.isConnected && (Date.now() - startTime) < maxWaitTime) {
          await new Promise(resolve => setTimeout(resolve, pollInterval))
          console.log('[REALTIME] 🔄 Still waiting for connection...', `${Math.round((Date.now() - startTime) / 1000)}s`)
          console.log('[REALTIME] 🔍 Current client state:', {
            hasClient: !!clientRef.current,
            isConnected: clientRef.current?.isConnected
          })
        }

        // Final check after polling
        if (!clientRef.current?.isConnected) {
          const elapsed = Math.round((Date.now() - startTime) / 1000)
          console.error('[REALTIME] ❌ Connection timeout after', elapsed, 'seconds')
          console.error('[REALTIME] 🔍 Final client state:', {
            hasClient: !!clientRef.current,
            isConnected: clientRef.current?.isConnected,
            clientMethods: clientRef.current ? Object.getOwnPropertyNames(clientRef.current) : 'No client'
          })
          throw new Error(`Client not connected after waiting ${elapsed}s. Please check your network connection and try again.`)
        }
      }

      console.log('[REALTIME] ✅ Client connected, requesting microphone...')

      // Request microphone permission
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: {
          channelCount: 1,
          sampleRate: 24000,
          echoCancellation: true,
          noiseSuppression: true,
          autoGainControl: true
        }
      })

      mediaStreamRef.current = stream
      console.log('[REALTIME] 🎤 Microphone access granted')

      // Create audio context and processor
      const audioContext = new AudioContext({ sampleRate: 24000 })
      audioContextRef.current = audioContext

      const source = audioContext.createMediaStreamSource(stream)
      // Buffer size reduced to 1024 for lower latency (~42ms)
      const processor = audioContext.createScriptProcessor(1024, 1, 1)
      processorRef.current = processor

      processor.onaudioprocess = (e) => {
        const inputData = e.inputBuffer.getChannelData(0)
        const outputData = new Int16Array(inputData.length)

        // Convert Float32Array to Int16Array
        for (let i = 0; i < inputData.length; i++) {
          const s = Math.max(-1, Math.min(1, inputData[i]))
          outputData[i] = s < 0 ? s * 0x8000 : s * 0x7FFF
        }

        // Send audio to OpenAI - check connection before each send
        if (clientRef.current?.isConnected) {
          lastSeenConnectedAtRef.current = Date.now()
          // Flush any locally buffered audio first
          if (localAudioBufferRef.current.length > 0) {
            for (const b of localAudioBufferRef.current) {
              try { clientRef.current.appendInputAudio(b) } catch { }
            }
            localAudioBufferRef.current = []
          }
          try {
            clientRef.current.appendInputAudio(outputData)
          } catch (err) {
            console.warn('[REALTIME] appendInputAudio failed, buffering', err)
            localAudioBufferRef.current.push(outputData)
          }
        } else {
          // Buffer locally and attempt reconnect for a short window
          if (localAudioBufferRef.current.length >= MAX_LOCAL_BUFFER_BLOCKS) {
            // keep newest data, drop oldest
            localAudioBufferRef.current.splice(0, localAudioBufferRef.current.length - MAX_LOCAL_BUFFER_BLOCKS + 1)
          }
          localAudioBufferRef.current.push(outputData)
          if (!reconnectingRef.current) {
            reconnectingRef.current = true
            const attemptReconnect = async () => {
              const start = Date.now()
              while (Date.now() - start < reconnectTimeoutMs) {
                try {
                  const current = clientRef.current as unknown as { isConnected?: boolean; connect?: () => Promise<void> } | null
                  if (current && !current.isConnected) {
                    await (current.connect?.())
                    if (current.isConnected) {
                      console.log('[REALTIME] Reconnected during audio streaming')
                      reconnectingRef.current = false
                      lastSeenConnectedAtRef.current = Date.now()
                      return
                    }
                  } else {
                    reconnectingRef.current = false
                    return
                  }
                } catch (e) {
                  console.warn('[REALTIME] reconnect attempt failed', e)
                }
                await new Promise(r => setTimeout(r, 500))
              }
              // If not reconnected after timeout — gracefully stop the mic once
              if (mediaStreamRef.current && !stoppedDueToDisconnectRef.current) {
                stoppedDueToDisconnectRef.current = true
                console.error('[REALTIME] ❌ Connection timeout after 5 seconds. Voice session stopped.')
                console.log('%c🔴 Realtime Voice Connection Failed', 'color: red; font-size: 14px; font-weight: bold;')
                console.log('%cThe OpenAI Realtime voice connection could not be established after 5 seconds. Please check your internet connection and try again.', 'color: orange; font-size: 12px;')
                try { processor.disconnect() } catch { }
                try { audioContext.close() } catch { }
                mediaStreamRef.current.getTracks().forEach(t => t.stop())
                mediaStreamRef.current = null
                setState(prev => ({ ...prev, isListening: false, error: '🔴 Voice connection timeout. Please check your connection and try again.' }))
              }
            }
            attemptReconnect().finally(() => { reconnectingRef.current = false })
          }
        }
      }

      source.connect(processor)
      // Route through a muted gain node to avoid any feedback/monitor artifacts
      const silentGain = audioContext.createGain()
      silentGain.gain.value = 0
      processor.connect(silentGain)
      silentGain.connect(audioContext.destination)

      console.log('[REALTIME] ✅ Voice session started successfully')
      setState(prev => ({ ...prev, isListening: true, error: null }))

    } catch (error) {
      console.error('[REALTIME] ❌ Failed to start voice session:', error)
      setState(prev => ({
        ...prev,
        isListening: false,
        error: error instanceof Error ? error.message : 'Failed to start voice session'
      }))
    }
  }, [initializeSession, state.isListening, conversationId])

  // Stop voice conversation
  const stopVoiceSession = useCallback(() => {
    console.log('[REALTIME] 🛑 Stopping voice session...')
    stoppedDueToDisconnectRef.current = false

    // Stop any active playback sources deterministically
    try {
      const list = activeSourcesRef.current
      if (list.length > 0) {
        console.log('[REALTIME-AUDIO] 🛑 Stopping', list.length, 'active source(s) on stop')
        while (list.length) {
          const s = list.pop()!
          try { s.stop(0) } catch { }
          try { s.disconnect() } catch { }
        }
      }
      playbackQueueRef.current = []
      isPlayingRef.current = false
    } catch { }

    // Stop microphone input processor
    if (processorRef.current) {
      try {
        processorRef.current.disconnect()
        console.log('[REALTIME] ✅ Processor disconnected')
      } catch (err) {
        console.warn('[REALTIME] Processor disconnect error:', err)
      }
      processorRef.current = null
    }

    // Close microphone audio context
    if (audioContextRef.current) {
      try {
        if (audioContextRef.current.state !== 'closed') {
          audioContextRef.current.close()
          console.log('[REALTIME] ✅ Audio context closed')
        } else {
          console.log('[REALTIME] Audio context already closed')
        }
      } catch (err) {
        console.warn('[REALTIME] Audio context close error:', err)
      }
      audioContextRef.current = null
    }

    // DON'T close playback context - keep it for AI responses!
    // if (playbackContextRef.current) {
    //   playbackContextRef.current.close().catch(console.error)
    //   playbackContextRef.current = null
    // }

    // Stop microphone tracks
    if (mediaStreamRef.current) {
      mediaStreamRef.current.getTracks().forEach(track => {
        track.stop()
        console.log('[REALTIME] ✅ Microphone track stopped')
      })
      mediaStreamRef.current = null
    }

    // Clear audio queue
    playbackQueueRef.current = []
    isPlayingRef.current = false

    console.log('[REALTIME] ✅ Voice session stopped (WebSocket stays connected)')
    setState(prev => ({ ...prev, isListening: false, isSpeaking: false }))
  }, [])

  // Send text message (for hybrid mode)
  const sendTextMessage = useCallback(async (text: string) => {
    if (!clientRef.current || !text.trim()) return

    try {
      clientRef.current.sendUserMessageContent([
        { type: 'input_text', text }
      ])
    } catch (error) {
      console.error('[REALTIME] Failed to send text:', error)
      setState(prev => ({
        ...prev,
        error: 'Failed to send message'
      }))
    }
  }, [])

  // Toggle voice listening
  const toggleVoiceSession = useCallback(async () => {
    console.log('[REALTIME] 🔄 Toggle requested, currently listening:', state.isListening)

    if (state.isListening) {
      stopVoiceSession()
    } else {
      await startVoiceSession()
    }
  }, [state.isListening, startVoiceSession, stopVoiceSession])

  // Clear error
  const clearError = useCallback(() => {
    setState(prev => ({ ...prev, error: null }))
  }, [])

  // Mount/Unmount logging + Cleanup
  useEffect(() => {
    const instanceId = mountIdRef.current
    console.log('[REALTIME] ✅ Mounted instance:', instanceId)

    return () => {
      console.log('[REALTIME] 🧹 Unmounting instance:', instanceId)

      // Remove client event handlers if present
      try { cleanupHandlersRef.current?.() } catch { }

      if (processorRef.current) {
        try { processorRef.current.disconnect() } catch { }
      }

      // Stop active sources on unmount
      try {
        const list = activeSourcesRef.current
        while (list.length) {
          const s = list.pop()!
          try { s.stop(0) } catch { }
          try { s.disconnect() } catch { }
        }
        playbackQueueRef.current = []
      } catch { }

      if (audioContextRef.current) {
        try {
          if (audioContextRef.current.state !== 'closed') {
            audioContextRef.current.close()
          }
        } catch { }
      }

      if (playbackContextRef.current) {
        try {
          if (playbackContextRef.current.state !== 'closed') {
            playbackContextRef.current.close()
          }
        } catch { }
      }

      if (mediaStreamRef.current) {
        mediaStreamRef.current.getTracks().forEach(track => track.stop())
      }

      if (clientRef.current) {
        try {
          console.log('[REALTIME] 🔌 Disconnecting WebSocket on unmount')
          clientRef.current.disconnect()
        } catch (err) {
          console.warn('[REALTIME] Disconnect error:', err)
        }
      }
    }
  }, [])

  // Sync isMutedRef with isMuted state
  useEffect(() => {
    isMutedRef.current = isMuted
  }, [isMuted])

  // Handle conversation ID changes - reinitialize when conversation becomes available
  useEffect(() => {
    if (conversationId && user && !isAuthLoading) {
      console.log('[REALTIME] 🔄 Conversation ID changed to:', conversationId, '- reinitializing if needed')
      // Only initialize if not already connected
      if (!clientRef.current || !clientRef.current.isConnected) {
        initializeSession()
      }
    } else if (!conversationId) {
      console.log('[REALTIME] 🔄 Conversation ID cleared - stopping session if active')
      // Stop session if conversation ID is cleared
      if (clientRef.current && clientRef.current.isConnected) {
        stopVoiceSession()
      }
    }
  }, [conversationId, user, isAuthLoading, initializeSession, stopVoiceSession])

  const toggleMute = useCallback(() => {
    const now = Date.now()

    // Debounce: ignore toggles within 250ms of the last one
    if (now - lastToggleAtRef.current < 250) {
      console.log('[REALTIME-AUDIO] Ignoring rapid toggle (debounced)')
      return
    }
    lastToggleAtRef.current = now

    setIsMuted(prev => {
      const newMuted = !prev

      // Idempotent check: if already in this state, skip side effects
      if (newMuted === isMutedRef.current) {
        console.log('[REALTIME-AUDIO] Toggle no-op, already', newMuted)
        return prev
      }

      // Update ref immediately so other concurrent callers see it
      isMutedRef.current = newMuted
      console.log('[REALTIME-AUDIO] Mute toggled:', newMuted)

      if (newMuted) {
        // Clear queue only once when muting
        playbackQueueRef.current = []
        console.log('[REALTIME-AUDIO] Queue cleared due to mute')
      }

      return newMuted
    })
  }, [])

  // Live-update the OpenAI session voice without reinitializing the client
  const updateRealtimeVoice = useCallback(async (voice: string) => {
    try {
      if (clientRef.current?.isConnected) {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        await (clientRef.current as any).updateSession({ voice })
        console.log('[REALTIME] 🎤 Updated session voice to:', voice)
      } else {
        console.log('[REALTIME] ℹ️ Client not connected; voice will apply on next session start:', voice)
      }
    } catch (err) {
      console.error('[REALTIME] ❌ Failed to update session voice:', err)
    }
  }, [])

  return {
    ...state,
    isMuted,
    toggleMute,
    startVoiceSession,
    stopVoiceSession,
    toggleVoiceSession,
    sendTextMessage,
    clearError,
    initializeSession,
    updateRealtimeVoice
  }
}
